/**
 * Helper centralizat pentru formatarea și parsarea numerelor în aplicație.
 *
 * Format afișare: en-US (1,234.56) SAU ro-RO (1.234,56), per preferința
 * userului. `activeLocale` e setat la runtime din `useFormatPrefsStore` prin
 * <NumberFormatProvider> (setNumberLocale). Default en-US. Funcțiile rămân pure
 * relativ la argumente → call-site-urile NU se schimbă când comuți locale-ul.
 *
 * Datele calendaristice NU folosesc acest helper — rămân în format ro-RO
 * (toLocaleDateString('ro-RO') → 31.12.2026).
 */

export type NumberLocale = 'en-US' | 'ro-RO';

/** Locale activ pentru afișarea numerelor (mutabil la runtime, default en-US). */
let activeLocale: NumberLocale = 'en-US';
export function setNumberLocale(locale: NumberLocale): void { activeLocale = locale; }
export function getNumberLocale(): NumberLocale { return activeLocale; }

/**
 * Formatează un număr pentru afișare.
 *
 * @param value - valoarea numerică (sau string parsabil)
 * @param decimals - numărul fix de zecimale (default 2)
 * @returns string formatat, ex. formatNumber(1299.317, 2) → "1,299.32"
 */
export function formatNumber(value: number | string | null | undefined, decimals: number = 2): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(activeLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formatează un număr cu număr variabil de zecimale (fără trailing zeros).
 * Util pentru prețuri unitare unde priceDecimals e dinamic.
 */
export function formatNumberFlex(value: number | string | null | undefined, maxDecimals: number = 2, minDecimals: number = 0): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(activeLocale, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Formatează un număr ca sumă monetară cu simbolul monedei.
 *
 * @example formatCurrency(1299.31, 'RON') → "1,299.31 RON"
 */
export function formatCurrency(value: number | string | null | undefined, currency: string = 'RON', decimals: number = 2): string {
  return formatNumber(value, decimals) + ' ' + currency;
}

/**
 * Parsează o valoare de input utilizator într-un număr.
 *
 * Tolerant: acceptă atât formatul en-US (1,299.31) cât și ro-RO (1.299,31)
 * pentru utilizatorii care mai introduc valori cu virgulă.
 *
 * Reguli:
 *  - Dacă șirul conține atât . cât și ,: ultima apariție e separator zecimal,
 *    celelalte sunt separatori de mii.
 *  - Dacă conține doar , sau doar .: interpretat ca zecimal dacă apare o singură
 *    dată cu 1-3 cifre după; altfel e separator de mii (rar — majoritatea
 *    inputurilor sunt numere <100000 cu max 4 zecimale).
 *  - Caractere non-numerice sunt ignorate (spații, simboluri monedă).
 *
 * @returns număr parsat sau 0 dacă inputul nu e valid
 */
export function parseNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value == null) return 0;

  const raw = String(value).trim();
  if (!raw) return 0;

  // Păstrăm doar cifre, . , - (semn negativ inițial)
  const cleaned = raw.replace(/[^\d.,\-]/g, '');
  if (!cleaned) return 0;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');

  let normalized: string;

  if (lastDot === -1 && lastComma === -1) {
    normalized = cleaned;
  } else if (lastDot !== -1 && lastComma !== -1) {
    // Ambele separatoare: ultima apariție e zecimal, celelalte sunt mii
    const decimalSep = lastDot > lastComma ? '.' : ',';
    const thousandSep = decimalSep === '.' ? ',' : '.';
    normalized = cleaned.split(thousandSep).join('').replace(decimalSep, '.');
  } else {
    // Doar un tip de separator
    const sep = lastDot !== -1 ? '.' : ',';
    const parts = cleaned.split(sep);
    if (parts.length === 2 && parts[1].length <= 4) {
      // Interpretat ca zecimal (ex. "9,99" sau "9.999")
      normalized = parts[0] + '.' + parts[1];
    } else {
      // Separator de mii (ex. "1,000,000" sau "1.000.000")
      normalized = parts.join('');
    }
  }

  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Returnează numărul maxim de zecimale efective (fără trailing zeros) dintr-o
 * listă de valori, plafonat la `cap` (default 4).
 *
 * Util pentru a deduce precizia de afișare a prețurilor unitare dintr-un
 * document salvat sau importat (ex. NIR vechi fără priceDecimals persistat,
 * sau linii importate din e-Factura / comandă furnizor).
 *
 * @example
 *  detectDecimals(['12.3456', 9.9, 5])   // 4
 *  detectDecimals([10, 20])              // 0
 *  detectDecimals(['1.23456'])           // 4 (plafonat de la 5)
 *  detectDecimals([])                    // 0
 */
export function detectDecimals(values: Array<number | string | null | undefined>, cap: number = 4): number {
  let max = 0;
  for (const v of values) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    // toString redă forma canonică fără trailing zeros (12.3000 → "12.3").
    const s = Math.abs(n).toString();
    const dot = s.indexOf('.');
    if (dot === -1) continue;
    const len = s.length - dot - 1;
    if (len > max) max = len;
  }
  return Math.min(max, cap);
}

/**
 * Variantă a parseNumber care returnează null pentru input invalid/gol.
 * Util pentru câmpuri opționale sau cu fallback-uri diferite de 0.
 *
 * @example
 *  parseNumberOrNull('9.99') // 9.99
 *  parseNumberOrNull('9,99') // 9.99
 *  parseNumberOrNull('')     // null
 *  parseNumberOrNull('abc')  // null
 *  parseNumberOrNull('0')    // 0
 */
export function parseNumberOrNull(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  // Verific că există cel puțin o cifră în input
  if (!/\d/.test(raw)) return null;
  const parsed = parseNumber(raw);
  // parseNumber returnează 0 pentru invalid, dar și pentru '0' legitim
  // Dacă rezultatul e 0 și inputul nu conținea '0', e invalid
  if (parsed === 0 && !/0/.test(raw)) return null;
  return parsed;
}
